import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, Button, Skeleton, Typography } from '@mui/material';

import asyncService from '../../../../../datahub/asyncService';
import GreenCircleCheckIcon from '../../../../assets/icons/GreenCircleCheckIcon';
import JobBadgeIcon from '../../../../assets/icons/JobBadgeIcon';
import PurpleInfoCircleIcon from '../../../../assets/icons/PurpleInfoCircleIcon';
import RedCalendarCrossIcon from '../../../../assets/icons/RedCalendarCrossIcon';
import CommonTooltip from '../../../../components/Tooltip/Tooltip';
import INDUSTRY_OPTIONS from '../../../../constants/industryConstants';
import selectUser from '../../../../store/selectors/appSelector';
import { BLACK } from '../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../theme/fontConstants';
import { fractionToPercent } from '../../../../utils/miscHelpers';

import { getReliabilityScoreColors } from './reliabilityScoreConstants';
import ReliabilityScoreReportBadge from './ReliabilityScoreReportBadge';

const WORKER_REPORT_ROUTE = 'users/get-worker-stats';

const INDUSTRY_LABEL_KEYS = {
  [INDUSTRY_OPTIONS.HEALTHCARE]: 'jobOrder.roster.workerReport.industry.healthcare',
  [INDUSTRY_OPTIONS.MANUFACTURING]: 'jobOrder.roster.workerReport.industry.manufacturing',
  [INDUSTRY_OPTIONS.HOSPITALITY]: 'jobOrder.roster.workerReport.industry.hospitality',
};

const sectionTitleSx = {
  fontFamily: PRIMARY_FONT[400],
  fontSize: '14px',
  color: BLACK[100],
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
  lineHeight: 1.4,
};

const mainNumberSx = {
  fontFamily: PRIMARY_FONT[700],
  fontSize: '24px',
  color: BLACK[100],
  lineHeight: 1.3,
};

const categoryTextSx = {
  fontFamily: PRIMARY_FONT[400],
  fontSize: '14px',
  color: BLACK[60],
  lineHeight: 1.5,
};

const categoryValueTextSx = {
  fontFamily: PRIMARY_FONT[400],
  fontSize: '14px',
  color: BLACK[100],
  lineHeight: 1.5,
};

const daysSinceSx = {
  fontFamily: PRIMARY_FONT[400],
  fontSize: '14px',
  color: BLACK[60],
  lineHeight: 1.5,
  marginTop: '8px',
};

const columnSx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const WorkerReport = ({
  reliabilityScore,
  employeeId,
  onAcceptApplication,
  onDeclineApplication,
}) => {
  const dispatch = useDispatch();
  const { token, refreshToken } = useSelector(selectUser);
  const [isLoading, setIsLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const { bgColor, reportBgColor } = getReliabilityScoreColors(reliabilityScore);

  const tokenRef = useRef(token);
  const refreshTokenRef = useRef(refreshToken);
  tokenRef.current = token;
  refreshTokenRef.current = refreshToken;

  const fetchWorkerReport = useCallback(() => {
    setIsLoading(true);

    asyncService({
      httpMethod: 'POST',
      route: WORKER_REPORT_ROUTE,
      data: { candidateId: employeeId },
      token: tokenRef.current,
      refreshToken: refreshTokenRef.current,
      dispatch,
      onSuccess: (response) => {
        setReportData(response?.data ?? null);
        setIsLoading(false);
      },
      onError: () => {
        setReportData(null);
        setIsLoading(false);
      },
    });
  }, [employeeId, dispatch]);

  useEffect(() => {
    fetchWorkerReport();
  }, [fetchWorkerReport]);

  const renderContentSkeleton = () => (
    <Box sx={{ display: 'flex', gap: '16px' }}>
      <Skeleton variant="rounded" width="33%" height={160} sx={{ borderRadius: '8px' }} />
      <Skeleton variant="rounded" width="33%" height={160} sx={{ borderRadius: '8px' }} />
      <Skeleton variant="rounded" width="33%" height={160} sx={{ borderRadius: '8px' }} />
    </Box>
  );

  const renderShiftsWorked = () => {
    const shiftsWorked = reportData?.shiftsWorked ?? {};
    const { total, ...industryFields } = shiftsWorked;
    const industries = Object.entries(industryFields).filter(([, count]) => count != null);

    return (
      <Box sx={columnSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
          <JobBadgeIcon />
          <Typography sx={sectionTitleSx}>
            {LanguageConverterV2('jobOrder.roster.workerReport.shiftsWorked')}
          </Typography>
        </Box>
        <Typography sx={mainNumberSx}>{total ?? '--'}</Typography>
        {industries.map(([key, count]) => (
          <Box
            key={key}
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Typography sx={categoryTextSx}>
              {INDUSTRY_LABEL_KEYS[key] ? LanguageConverterV2(INDUSTRY_LABEL_KEYS[key]) : key}
            </Typography>
            <Typography sx={categoryValueTextSx}>{count}</Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const renderShiftCompletionRate = () => {
    const rawCompletionPct = reportData?.shiftCompletionPct;
    const shiftCompletionPct =
      typeof rawCompletionPct === 'number' ? fractionToPercent(rawCompletionPct) : null;
    const totalScheduledShifts = reportData?.totalScheduledShifts;
    const totalNcns = reportData?.attendance?.totalNcns;
    const lastNcnsTimestamp = reportData?.attendance?.lastNcnsTimestamp;
    const ncnsDaysSince = lastNcnsTimestamp
      ? Math.floor((Date.now() - lastNcnsTimestamp) / 86400000)
      : null;

    return (
      <Box sx={columnSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
          <GreenCircleCheckIcon />
          <Typography sx={sectionTitleSx}>
            {LanguageConverterV2('jobOrder.roster.workerReport.shiftCompletionRate')}
          </Typography>
          <CommonTooltip
            title={
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  alignItems: 'center',
                  width: '220px',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: PRIMARY_FONT[700],
                    fontSize: '16px',
                    marginBottom: '4px',
                    color: BLACK[100],
                  }}
                >
                  {LanguageConverterV2('jobOrder.roster.workerReport.shiftCompletionTooltipTitle')}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: PRIMARY_FONT[400],
                    fontSize: '14px',
                    color: BLACK[60],
                    textAlign: 'center',
                  }}
                >
                  {LanguageConverterV2('jobOrder.roster.workerReport.shiftCompletionTooltip')}
                </Typography>
              </Box>
            }
            placement="top"
            bgcolor={BLACK[0]}
            color={BLACK[80]}
            enterDelay={200}
          >
            <PurpleInfoCircleIcon />
          </CommonTooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
          <Typography sx={mainNumberSx}>{shiftCompletionPct ?? '--'}</Typography>
          {shiftCompletionPct != null && (
            <Typography sx={{ ...mainNumberSx, fontSize: '20px' }}>%</Typography>
          )}
        </Box>
        <Typography sx={categoryTextSx}>
          {LanguageConverterV2('jobOrder.roster.workerReport.basedOnScheduledShifts', {
            count: totalScheduledShifts ?? '--',
          })}
        </Typography>
        <Typography sx={{ ...sectionTitleSx, marginTop: '12px', marginBottom: '4px' }}>
          {LanguageConverterV2('jobOrder.roster.workerReport.noCallNoShow')}
        </Typography>
        <Typography sx={mainNumberSx}>{totalNcns ?? '--'}</Typography>
        {totalNcns != null && (
          <Typography sx={daysSinceSx}>
            {totalNcns > 0
              ? LanguageConverterV2('jobOrder.roster.workerReport.daysSinceLastNcns', {
                  days: ncnsDaysSince ?? '--',
                })
              : LanguageConverterV2('jobOrder.roster.workerReport.noNcnsHistory')}
          </Typography>
        )}
      </Box>
    );
  };

  const renderEmployeeCancellations = () => {
    const {
      totalCandidateCancellations,
      totalAbsent,
      totalForgivenAbsent,
      lastCandidateCancellationTimestamp,
    } = reportData?.attendance ?? {};
    const cancellationDaysSince = lastCandidateCancellationTimestamp
      ? Math.floor((Date.now() - lastCandidateCancellationTimestamp) / 86400000)
      : null;

    return (
      <Box sx={columnSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
          <RedCalendarCrossIcon />
          <Typography sx={sectionTitleSx}>
            {LanguageConverterV2('jobOrder.roster.workerReport.employeeCancellations')}
          </Typography>
        </Box>
        <Typography sx={mainNumberSx}>{totalCandidateCancellations ?? '--'}</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={categoryTextSx}>
            {LanguageConverterV2('jobOrder.roster.workerReport.absent')}
          </Typography>
          <Typography sx={categoryValueTextSx}>{totalAbsent ?? '--'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={categoryTextSx}>
            {LanguageConverterV2('jobOrder.roster.workerReport.forgivenAbsent')}
          </Typography>
          <Typography sx={categoryValueTextSx}>{totalForgivenAbsent ?? '--'}</Typography>
        </Box>
        {totalCandidateCancellations != null && (
          <Typography sx={daysSinceSx}>
            {totalCandidateCancellations > 0
              ? LanguageConverterV2('jobOrder.roster.workerReport.daysSinceLastCancellation', {
                  days: cancellationDaysSince ?? '--',
                })
              : LanguageConverterV2('jobOrder.roster.workerReport.noCancellationHistory')}
          </Typography>
        )}
      </Box>
    );
  };

  const renderReportContent = () => (
    <Box
      flexDirection={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      sx={{
        display: 'flex',
        gap: '24px',
        padding: '16px',
      }}
    >
      {renderShiftsWorked()}
      <Box sx={{ width: '1px', backgroundColor: BLACK[40], flexShrink: 0 }} />
      {renderShiftCompletionRate()}
      <Box sx={{ width: '1px', backgroundColor: BLACK[40], flexShrink: 0 }} />
      {renderEmployeeCancellations()}
    </Box>
  );

  const showApplicationCta = Boolean(onAcceptApplication && onDeclineApplication);

  return (
    <Box
      sx={{
        borderTop: `1px solid ${BLACK[40]}`,
        borderRadius: '0 0 12px 12px',
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              backgroundColor: reportBgColor,
              padding: '12px 24px',
            }}
          >
            <ReliabilityScoreReportBadge score={reliabilityScore} />
            <Box>
              <Typography
                sx={{
                  fontFamily: PRIMARY_FONT[700],
                  fontSize: '16px',
                  color: bgColor,
                  lineHeight: 1.4,
                }}
              >
                {LanguageConverterV2('jobOrder.roster.workerReport.title')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: PRIMARY_FONT[400],
                  fontSize: '14px',
                  color: BLACK[70],
                  lineHeight: 1.5,
                }}
              >
                {LanguageConverterV2('jobOrder.roster.workerReport.description')}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ padding: '0px 24px 24px 24px' }}>
            {isLoading ? renderContentSkeleton() : renderReportContent()}
          </Box>
        </Box>
      </Box>
      {showApplicationCta && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: `1px solid ${BLACK[40]}`,
            padding: '12px 24px',
          }}
        >
          <Typography
            sx={{
              fontFamily: PRIMARY_FONT[400],
              fontSize: '14px',
              color: BLACK[80],
            }}
          >
            {LanguageConverterV2('jobOrder.roster.workerReport.applicationPrompt')}
          </Typography>
          <Box sx={{ display: 'flex', gap: '12px' }}>
            <Button
              variant="contained"
              onClick={onAcceptApplication}
              sx={{
                fontFamily: PRIMARY_FONT[500],
                fontSize: '14px',
              }}
            >
              {LanguageConverterV2('jobOrder.roster.status.accept')}
            </Button>
            <Button
              variant="outlined"
              onClick={onDeclineApplication}
              sx={{
                fontFamily: PRIMARY_FONT[500],
                fontSize: '14px',
              }}
            >
              {LanguageConverterV2('jobOrder.roster.status.decline')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

WorkerReport.propTypes = {
  reliabilityScore: PropTypes.number,
  employeeId: PropTypes.string,
  onAcceptApplication: PropTypes.func,
  onDeclineApplication: PropTypes.func,
};

export default WorkerReport;
